import { push } from "redux-first-history";

import API from "api";
import { NetworkRequest } from "api/actions";
import { PersonActionCreators, PersonActions } from "modules/person/actionCreator";
import { actionCreators as InboxActionCreators, KnownActions as InboxActions } from "modules/inbox/actionCreator";
import { ApplicationState as AppState, AppThunkAction } from "store";
import * as actions from "./actions";
import { AuthResponse, RefreshFailure, VerifyOtpFailure, MIN_SECONDS_VALUE, MINUTES_THRESHOLD } from "./models";
import { endSessionThroughLogOut } from "./sessionEnding";
import FeatureToggleChecker from "modules/utils/featureToggleChecker";
import { signalRConnection } from "signalr";
import { RouterAction } from "routes";
import { Formatter } from "modules/utils";

export type DownstreamActions = PersonActions | InboxActions;

export type HandledAction =
    | actions.LogInInitAction
    | actions.LogInCompleteAction
    | actions.LogInFirstStage2faAction
    | actions.VerifyOtpInitAction
    | actions.VerifyOtpCompleteAction
    | actions.ResendOtpInitAction
    | actions.ResendOtpCompleteAction
    | actions.ResendOtpErrorAction
    | actions.ReauthenticateInitAction
    | actions.ReauthenticateCompleteAction
    | actions.RefreshAccessTokenInitAction
    | actions.RefreshAccessTokenCompleteAction
    | actions.SetUserIdleStatus
    | actions.ApplicationInitAction;

export type AuthActions = HandledAction | DownstreamActions | RouterAction | NetworkRequest;

const MILLISECONDS_CONST = 1000;
const MINUTES_CONST = MILLISECONDS_CONST * 60;

export const RefreshToken = {
    promise: null
};

function userIsAuthenticated(state: AppState): boolean {
    const session = state.auth?.session;
    // A first-stage MFA user holds a session but has not completed OTP, so they are not yet authenticated.
    return !!session?.userId && !!session?.accessTokenExpiry && !session?.isFirstStage2fa;
}

function shouldReauthenticate(state: AppState): boolean {
    const expiry = state.auth.session.accessTokenExpiry;
    const exp = expiry < MIN_SECONDS_VALUE ? expiry * MILLISECONDS_CONST : expiry;
    const now = new Date().getTime();
    const isExpired = exp < now || exp - now < MINUTES_CONST * MINUTES_THRESHOLD;
    return isExpired;
}

export const AuthActionCreators = {
    logIn:
        (username: string, password: string, returnUrl?: string): AppThunkAction<AuthActions> =>
        (dispatch, getState) => {
            dispatch({ type: "LOG_IN_INIT" });

            const task = dispatch(API.authentication.logIn(username, password))
                .then((response) => {
                    if (response?.status === 200) {
                        (response.json() as Promise<AuthResponse>)
                            .then((authResponse: AuthResponse) => {
                                // For 2FA login, the LOG_IN_COMPLETE will be called after OTP is verified
                                if (authResponse.isFirstStage2fa) {
                                    dispatch({
                                        type: "LOG_IN_FIRST_STAGE_2FA",
                                        authResponse,
                                        username
                                    });
                                    return null;
                                } else {
                                    dispatch({
                                        type: "LOG_IN_COMPLETE",
                                        username,
                                        authResponse,
                                        nonPilotUserLoggedIn: false,
                                        usedTwoFactor: false
                                    });
                                    dispatch(push(returnUrl || "/"));
                                    return authResponse;
                                }
                            })
                            .then((authResponse: AuthResponse) =>
                                authResponse ? dispatch(PersonActionCreators.getPerson(authResponse.userId)) : null
                            )
                            .then(() => {
                                const toggle = getState()?.features?.featureToggles?.EnableMessaging;
                                if (userIsAuthenticated(getState()) && FeatureToggleChecker.isOn(toggle)) {
                                    signalRConnection.connect();
                                    dispatch(InboxActionCreators.getConversations());
                                }
                            });
                    } else if (response?.status === 400 || response?.status === 401) {
                        dispatch({
                            type: "LOG_IN_COMPLETE",
                            username,
                            authResponse: {
                                succeeded: false,
                                message: "Your username or password is incorrect. Please try signing in again."
                            },
                            nonPilotUserLoggedIn: false,
                            usedTwoFactor: false
                        });
                    } else if (response?.status === 403) {
                        (response.json() as Promise<AuthResponse>).then((authResponse: AuthResponse) => {
                            if (authResponse.message === "not_verified") {
                                if (authResponse.isVerificationExpired) {
                                    dispatch({
                                        type: "LOG_IN_COMPLETE",
                                        username,
                                        authResponse: {
                                            succeeded: false,
                                            message:
                                                "Looks like your confirmation email has expired. You'll need to re-register - be sure to verify your account."
                                        },
                                        nonPilotUserLoggedIn: false,
                                        usedTwoFactor: false
                                    });
                                } else {
                                    dispatch({
                                        type: "LOG_IN_COMPLETE",
                                        username,
                                        authResponse: {
                                            succeeded: false,
                                            message: `Looks like you haven't confirmed your account yet. Please find the email we sent to ${
                                                authResponse.emailAddress
                                            } at ${Formatter.formatLocalDate(
                                                authResponse.emailSentDateTime,
                                                true,
                                                true
                                            )} and click the verification link.`,
                                            secondaryMessage: `After checking your spam folder, if you still can't find this email, please re-register your account.`
                                        },
                                        nonPilotUserLoggedIn: false,
                                        usedTwoFactor: false
                                    });
                                }
                            } else if (authResponse.message === "user_membership_terminated") {
                                dispatch({
                                    type: "LOG_IN_COMPLETE",
                                    username,
                                    authResponse: {
                                        succeeded: false,
                                        message: `Uh oh! We can't sign you in. Please contact ASEBP for assistance.`
                                    },
                                    nonPilotUserLoggedIn: false,
                                    usedTwoFactor: false
                                });
                            } else if (authResponse.message === "possible_arta_login") {
                                dispatch({
                                    type: "LOG_IN_COMPLETE",
                                    username,
                                    authResponse: { succeeded: false, artaLoginUrl: authResponse.artaLoginUrl },
                                    nonPilotUserLoggedIn: false,
                                    usedTwoFactor: false
                                });
                            } else {
                                dispatch({
                                    type: "LOG_IN_COMPLETE",
                                    username,
                                    authResponse: {
                                        succeeded: false,
                                        message: "Oops! We can't sign you in right now. Please try again later."
                                    },
                                    nonPilotUserLoggedIn: false,
                                    usedTwoFactor: false
                                });
                            }
                        });
                    } else {
                        dispatch({
                            type: "LOG_IN_COMPLETE",
                            username,
                            authResponse: {
                                succeeded: false,
                                message: "Oops! We can't sign you in right now. Please try again later."
                            },
                            nonPilotUserLoggedIn: false,
                            usedTwoFactor: false
                        });
                    }
                })
                .catch((error) => {
                    return console?.log(error);
                });

            return task;
        },
    verifyOtp:
        (otp: string, returnUrl?: string): AppThunkAction<AuthActions> =>
        (dispatch, getState) => {
            dispatch({ type: "VERIFY_OTP_INIT" });

            const task = dispatch(API.authentication.verifyOtp(otp.trim()))
                .then((response) => {
                    if (response?.status === 200) {
                        (response.json() as Promise<AuthResponse>)
                            .then((authResponse: AuthResponse) => {
                                dispatch({ type: "VERIFY_OTP_COMPLETE", authResponse });
                                dispatch({
                                    type: "LOG_IN_COMPLETE",
                                    username: authResponse.userId,
                                    authResponse,
                                    nonPilotUserLoggedIn: false,
                                    usedTwoFactor: true
                                });
                                dispatch(push(returnUrl || "/"));
                                return authResponse;
                            })
                            .then((authResponse: AuthResponse) =>
                                dispatch(PersonActionCreators.getPerson(authResponse.userId))
                            )
                            .then(() => {
                                const toggle = getState()?.features?.featureToggles?.EnableMessaging;
                                if (FeatureToggleChecker.isOn(toggle)) {
                                    signalRConnection.connect();
                                    dispatch(InboxActionCreators.getConversations());
                                }
                            });
                    } else if (response?.status === 401) {
                        // Two different 401s land here. `sessionEnded` marks the ones the member cannot
                        // recover from - 2FA stopped applying, or the pending sign in idled out - which
                        // the server has already signed them out of, so they go to the log in page. A
                        // wrong or expired code carries no body, which is what keeps a typo from
                        // ejecting someone who just needs another go.
                        return (response.json() as Promise<VerifyOtpFailure>)
                            .catch(() => ({} as VerifyOtpFailure))
                            .then((failure) => {
                                if (failure?.sessionEnded) {
                                    endSessionThroughLogOut();
                                    return;
                                }

                                dispatch({
                                    type: "VERIFY_OTP_COMPLETE",
                                    authResponse: {
                                        succeeded: false,
                                        message:
                                            "The code is invalid or has expired. Please try again or request a new code."
                                    },
                                    failureReason: "invalidOrExpired"
                                });
                            });
                    } else {
                        // The member is told the same thing as for a rejected code, but the server never
                        // judged this one, so it is not counted as a code the member got wrong.
                        dispatch({
                            type: "VERIFY_OTP_COMPLETE",
                            authResponse: {
                                succeeded: false,
                                message: "The code is invalid or has expired. Please try again or request a new code."
                            },
                            failureReason: "error"
                        });
                    }
                })
                .catch((error) => {
                    dispatch({
                        type: "VERIFY_OTP_COMPLETE",
                        authResponse: {
                            succeeded: false,
                            message: "Oops! We couldn't verify your code right now. Please try again later."
                        },
                        failureReason: "error"
                    });
                });

            return task;
        },
    resendOtp: (): AppThunkAction<AuthActions> => (dispatch) => {
        dispatch({ type: "RESEND_OTP_INIT" });

        const task = dispatch(API.authentication.resendOtp())
            .then((response) => {
                if (response?.status === 200) {
                    dispatch({
                        type: "RESEND_OTP_COMPLETE",
                        message: "A new code has been sent."
                    });
                } else if (response?.status === 401) {
                    // The only 401 a resend can get: the OtpAccess policy refusing the first stage
                    // cookie itself, since the endpoint carries no code and needs no live verifier.
                    // Nothing on this form can fix that, so end the session rather than invite a retry.
                    endSessionThroughLogOut();
                } else {
                    dispatch({
                        type: "RESEND_OTP_ERROR",
                        message: "We couldn't send a new code right now. Please try again in a moment."
                    });
                }
            })
            .catch((error) => {
                dispatch({
                    type: "RESEND_OTP_ERROR",
                    message: "We couldn't send a new code right now. Please try again in a moment."
                });
            });

        return task;
    },
    // Intended to log out the session without redirecting to the log in page.
    // This is called when the session timeout dialog is shown so that a browser
    // refresh does not bypass the need to log in.
    logOutSilently: (): AppThunkAction<AuthActions> => (dispatch, getState) => {
        dispatch(API.authentication.logOut()).then(() => {
            const toggle = getState()?.features?.featureToggles?.EnableMessaging;
            if (FeatureToggleChecker.isOn(toggle)) {
                signalRConnection.disconnect();
            }
        });
    },
    // This is similar to log in, but does not redirect to the dashboard after.
    // Used by the session timeout dialog to allow the user to reauth and keep working where they are.
    reauthenticate:
        (password: string): AppThunkAction<AuthActions> =>
        (dispatch, getState) => {
            dispatch({ type: "REAUTHENTICATE_INIT" });

            const username = getState().auth.session.userId;
            const task = dispatch(API.authentication.reauthenticate(username, password)).then((response) => {
                if (response?.status === 200) {
                    (response.json() as Promise<AuthResponse>)
                        .then((authResponse: AuthResponse) => {
                            dispatch({ type: "REAUTHENTICATE_COMPLETE", authResponse });
                            return authResponse;
                        })
                        .then(() => {
                            const toggle = getState()?.features?.featureToggles?.EnableMessaging;
                            if (FeatureToggleChecker.isOn(toggle)) {
                                signalRConnection.connect();
                                dispatch(InboxActionCreators.getConversations(true));
                            }
                        });
                } else if (response?.status === 400 || response?.status === 401) {
                    dispatch({
                        type: "REAUTHENTICATE_COMPLETE",
                        authResponse: {
                            succeeded: false,
                            message: "Password is incorrect. Please try signing in again."
                        }
                    });
                } else {
                    dispatch({
                        type: "REAUTHENTICATE_COMPLETE",
                        authResponse: {
                            succeeded: false,
                            message: "Oops! We can't sign you in right now. Please try again later."
                        }
                    });
                }
            });

            return task;
        },
    refresh: (): AppThunkAction<AuthActions> => (dispatch, getState) => {
        const state = getState();

        if (RefreshToken.promise != null) {
            return RefreshToken.promise;
        }

        // Read once, here, rather than when the response lands. The idle path signs the
        // cookie out on purpose and offers to reauthenticate in place, so a 401 belonging
        // to that period must not end a session the member has since re-established.
        const wasIdleWhenIssued = state.auth.session.idle;

        if (userIsAuthenticated(state) && shouldReauthenticate(state)) {
            dispatch({ type: "REFRESH_ACCESS_TOKEN_INIT" });

            RefreshToken.promise = Promise.resolve(true).then(
                () => {
                    return dispatch(API.authentication.refreshToken()).then(
                        (response) => {
                            RefreshToken.promise = null;
                            if (response?.status === 200) {
                                return (response.json() as Promise<AuthResponse>).then((authResponse: AuthResponse) => {
                                    dispatch({ type: "REFRESH_ACCESS_TOKEN_COMPLETE", authResponse });
                                    return true;
                                });
                            } else if (response?.status === 401) {
                                dispatch({
                                    type: "REFRESH_ACCESS_TOKEN_COMPLETE",
                                    authResponse: {
                                        succeeded: false,
                                        message: "Failed to refresh token. Session must be expired."
                                    }
                                });

                                return (response.json() as Promise<RefreshFailure>)
                                    .catch(() => ({} as RefreshFailure))
                                    .then((failure) => {
                                        // A rejected grant (sessionMissing false) means this
                                        // session can never be recovered, so end it rather than
                                        // leaving a broken authenticated state.
                                        //
                                        // `sessionMissing` instead means the server had no cookie
                                        // to refresh. That covers a genuinely expired cookie, but
                                        // also - deliberately - the case where ANOTHER tab cleared
                                        // the shared cookie by signing out or idling out (idle
                                        // POSTs /api/authentication/logout). This tab may be active
                                        // and mid-task, so we fail the one request rather than sign
                                        // it out from under the member; activity keeps the sliding
                                        // cookie alive and the idle dialog is the recovery path.
                                        // Do NOT "fix" this to sign out here - it would kick an
                                        // active tab whenever a background tab idles.
                                        if (!wasIdleWhenIssued && !failure?.sessionMissing) {
                                            endSessionThroughLogOut();
                                        }
                                        return false;
                                    });
                            } else {
                                dispatch({
                                    type: "REFRESH_ACCESS_TOKEN_COMPLETE",
                                    authResponse: { succeeded: false, message: "Unable to refresh token." }
                                });
                                return false;
                            }
                        },
                        () => {
                            RefreshToken.promise = null;
                            return false;
                        }
                    );
                },
                () => {
                    RefreshToken.promise = null;
                    return false;
                }
            );
        }
        return RefreshToken.promise || Promise.resolve(true);
    },
    extendSession: (): AppThunkAction<AuthActions> => (dispatch, getState) => {
        dispatch(API.authentication.extendSession());
    },
    setUserIdle:
        (idle: boolean): AppThunkAction<AuthActions> =>
        (dispatch, getState) => {
            dispatch({ type: "SET_USER_IDLE", idle });
            const toggle = getState()?.features?.featureToggles?.EnableMessaging;
            if (FeatureToggleChecker.isOn(toggle)) {
                dispatch({ type: "SET_CONVERSATION_MODAL_OPEN", isOpen: false });
            }
        }
};
